import React, { useCallback, useEffect, useState, useMemo } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { DeliveryOptions } from './DeliveryOptions';
import { useSearchModelData } from '../../hooks/useSearchModelData';
import { FreeDeliveryToggle } from './FreeDeliveryToggle';
import {
  getDeliveryDimension, getSingleDeliveryRefinement, generateDeliveryOptions,
  isTodayOrByTomorrowEligibleInSomeDimensions,
  areAnyRefinementsEligibleInDimensions
} from '../../utils/deliveryUtils';
import {
  getAppliedDeliveryDimension
} from '../../utils/fulfillment-helpers';
import { DELIVERY_REFINEMENTS, FULFILLMENT_DIMENSIONS_LABELS, FULFILLMENT_UI_LABELS } from '../../utils/constants';

const {
  bool, func, string, object
} = PropTypes;

const DeliveryRefinements = ({
  className,
  customStyles,
  onDimensionChange,
  onRefinementChange,
  skipDeliveryAutoSelect,
  addMarkupForSeo,
  deselect
}) => {
  const { data } = useSearchModelData();
  const { dimensions = [], appliedDimensions = [] } = data?.searchModel || {};
  const freeDeliveryRefinement = getSingleDeliveryRefinement(
    dimensions, FULFILLMENT_DIMENSIONS_LABELS.FREE_DELIVERY,
    DELIVERY_REFINEMENTS.ANYTIME
  );
  // replaced isFreeDeliveryChecked value with real data check to determine
  // if free delivery is checked based on applied dimensions data from search model
  const [localIsFreeDeliveryChecked, setLocalIsFreeDeliveryChecked] = useState(appliedDimensions.some((dim) => dim.label === FULFILLMENT_UI_LABELS.FREE_DELIVERY));
  const [uiSelectedOption, setUiSelectedOption] = useState('');
  // maintaining local state for triggerDeliverySpeedChange, setTriggerDeliverySpeedChange context data, as only this component child needs this data
  const [triggerDeliverySpeedChange, setTriggerDeliverySpeedChange] = useState(null);

  // replacement of deliveryOptions context
  // computing deliveryOptions locally as we need only need this method only for this component
  const deliveryOptions = useMemo(
    () => generateDeliveryOptions(dimensions, FULFILLMENT_DIMENSIONS_LABELS.DELIVERY),
    [dimensions]
  );

  // replacement of hideDeliveryOptions context
  // computing hideDeliveryOptions locally as we need only need this method only for this component
  const hideDeliveryOptions = useMemo(
    () => !isTodayOrByTomorrowEligibleInSomeDimensions(
      dimensions,
      [FULFILLMENT_DIMENSIONS_LABELS.DELIVERY, FULFILLMENT_DIMENSIONS_LABELS.FREE_DELIVERY]
    ), [dimensions]);

  // replacement of freeDeliveryUnavailable context
  // computing freeDeliveryUnavailable locally as we need only need this method only for this component
  const freeDeliveryUnavailable = useMemo(() => !areAnyRefinementsEligibleInDimensions(
    dimensions,
    [FULFILLMENT_DIMENSIONS_LABELS.FREE_DELIVERY],
    [DELIVERY_REFINEMENTS.ANYTIME, DELIVERY_REFINEMENTS.TODAY, DELIVERY_REFINEMENTS.BY_TOMORROW]
  ), [dimensions]);

  const handleFreeDeliveryToggle = useCallback((isTrue) => {
    setLocalIsFreeDeliveryChecked(isTrue);
    const newDeliveryDimensionLabel = isTrue
      ? FULFILLMENT_UI_LABELS.FREE_DELIVERY : FULFILLMENT_UI_LABELS.DELIVERY;

    const appliedDeliveryDimension = getAppliedDeliveryDimension(appliedDimensions);
    const appliedRefinementLabel = appliedDeliveryDimension?.refinements[0]?.label;

    const dimension = getDeliveryDimension(
      dimensions,
      newDeliveryDimensionLabel
    );

    let refinement = getSingleDeliveryRefinement(
      dimensions,
      newDeliveryDimensionLabel,
      uiSelectedOption || appliedRefinementLabel || DELIVERY_REFINEMENTS.ANYTIME
    );

    refinement = { ...refinement, isToggle: true };
    onDimensionChange({ refinement, dimension });
  }, [
    onDimensionChange,
    appliedDimensions,
    dimensions,
    uiSelectedOption
  ]);

  const handleRefinementOptionChange = useCallback((label) => {
    setUiSelectedOption(label);

    const currentDeliveryDimension = localIsFreeDeliveryChecked
      ? FULFILLMENT_UI_LABELS.FREE_DELIVERY : FULFILLMENT_UI_LABELS.DELIVERY;

    if (onRefinementChange) {
      const dimension = getDeliveryDimension(
        dimensions,
        currentDeliveryDimension
      );
      const selectedRefinement = getSingleDeliveryRefinement(
        dimensions,
        currentDeliveryDimension,
        label
      );

      if (!selectedRefinement) {
        return;
      }

      onRefinementChange({
        refinement: {
          label,
          url: selectedRefinement?.url,
          refinementKey: selectedRefinement?.refinementKey
        },
        dimension: {
          ...dimension,
          label: currentDeliveryDimension
        }
      });
    }
  }, [onRefinementChange, dimensions, localIsFreeDeliveryChecked]);

  useEffect(() => {
    // Reset states if deselect is true
    if (deselect) {
      setLocalIsFreeDeliveryChecked(false);
      // can remove these two contexts as this code is unreachable currently due to deselect being false by default and not being set to true anywhere in the codebase.
      // ***********This is added as a safety measure in case deselect prop is used in the future
      // ********check these two states logic later ********/
      // setIsFreeDeliveryChecked(false);
      // setSelectedDeliveryDimension('delivery');
    // } else if (forceInitialSync) {
    //   // Sync local UI with context state when forceInitialSync is true
    //   syncUi(appliedDimensions);
    //   setForceInitialSync(false);
    } else {
      // Sync local UI with context state when appliedDimensions changes
      const appliedDeliveryDimension = getAppliedDeliveryDimension(appliedDimensions);
      setUiSelectedOption('');
      setLocalIsFreeDeliveryChecked(appliedDeliveryDimension?.label === FULFILLMENT_UI_LABELS.FREE_DELIVERY);
      setTriggerDeliverySpeedChange({ value: appliedDeliveryDimension?.refinements[0]?.label, localOnly: true });
    }
  }, [
    appliedDimensions,
    deselect,
    setTriggerDeliverySpeedChange
  ]);

  return (
    <div
      className={classNames('sui-flex sui-flex-col sui-gap-x-4 sui-gap-y-3 sui-w-full [&>label]:sui-mx-0', className)}
      style={customStyles}
    >
      {!freeDeliveryUnavailable && (
        <>
          {/* SEO-friendly hidden link - Required for SEO significance. Reach out to SEO team if wanting to change */}
          {addMarkupForSeo && (
            <div data-testid="emt-link-free-delivery">
              <a
                href={freeDeliveryRefinement?.url}
                className="sui-hidden"
                aria-hidden="true"
                tabIndex="-1"
              >
                {/* H3 element required for SEO significance. Reach out to SEO team if wanting to change */}
                <h3>{FULFILLMENT_UI_LABELS.FREE_DELIVERY}</h3>
              </a>
            </div>
          )}
          <FreeDeliveryToggle isChecked={localIsFreeDeliveryChecked} onToggle={handleFreeDeliveryToggle} />
        </>
      )}
      {!hideDeliveryOptions && deliveryOptions?.length > 0 && (
        <DeliveryOptions
          onChange={handleRefinementOptionChange}
          options={deliveryOptions}
          skipAutoSelect={skipDeliveryAutoSelect && deselect}
          triggerDeliverySpeedChange={triggerDeliverySpeedChange}
          setTriggerDeliverySpeedChange={setTriggerDeliverySpeedChange}
          deselect={deselect}
        />
      )}
    </div>
  );
};

DeliveryRefinements.propTypes = {
  className: string,
  // eslint-disable-next-line react/forbid-prop-types
  customStyles: object,
  onDimensionChange: func.isRequired,
  onRefinementChange: func,
  skipDeliveryAutoSelect: bool,
  addMarkupForSeo: bool,
  deselect: bool
};

DeliveryRefinements.defaultProps = {
  className: '',
  customStyles: {},
  onRefinementChange: null,
  skipDeliveryAutoSelect: false,
  addMarkupForSeo: false,
  deselect: false
};

DeliveryRefinements.displayName = 'DeliveryRefinements';

export { DeliveryRefinements };